import { useTranslation } from "react-i18next";
import { Link, useSearchParams } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";
import { Button, DatePicker, Table, Badge, Modal, message, Typography } from "antd";
import { all_routes } from "../../../../../router/all_routes";
import LoadingSpinner from "../../../../shared/uiInterface/base-ui/LoadingSpinner";
import dayjs from "dayjs";
import dailyRecordService from "../../../../../services/dailyRecord/dailyRecord.service";
import { UserRecordDto } from "../../../../../types/attendanceDto.types";
import { handleApiError } from "../../../../../utils/errorHandler";
import FooterSection from "../../../../shared/components/footerSection";
import i18n from "../../../../../i18n/config";
import datePickerTr from 'antd/lib/date-picker/locale/tr_TR';
import datePickerEn from 'antd/lib/date-picker/locale/en_US';
import { PermissionType, User } from "../../../../../types/user.types";
import userService from "../../../../../services/user/user.service";
import { createPdf, exportToExcel, normalizeTurkishChars } from '../../../../../utils/exportUtils';
import { useSelector } from "react-redux";
import { RootState } from "../../../../../store/store";

const { RangePicker } = DatePicker;
const { Title, Text } = Typography;


type EditedAttendanceRecord = {
    entryTime: dayjs.Dayjs | null;
    exitTime: dayjs.Dayjs | null;
    recordId?: number | string | null; // recordId'yi de ekleyelim
};
// Devam kayıtları için arayüz
interface AttendanceRecord {
    entryTime: string;
    exitTime: string;
    isLate: boolean;
    lateDuration: string;
    workMinutes: number;
}

const AllAttendanceUser = () => {
    const { t } = useTranslation();
    const [searchParams] = useSearchParams();
    const userId = searchParams.get('user');
    const [userRecords, setUserRecords] = useState<UserRecordDto>();
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [isEditModalVisible, setIsEditModalVisible] = useState(false);
    const [isUpdatingRecord, setIsUpdatingRecord] = useState(false);
    const [selectedRecord, setSelectedRecord] = useState<UserRecordDto['records'][0] | null>(null);
    const [editedRecord, setEditedRecord] = useState<EditedAttendanceRecord | null>(null);
    const [userData, setUserData] = useState<User | null>(null);
    const [dateRange, setDateRange] = useState<[dayjs.Dayjs, dayjs.Dayjs]>([
        dayjs().startOf('month'),
        dayjs().endOf('month')
    ]);
    const [statusFilter, setStatusFilter] = useState<'all' | 'onTime' | 'late'>('all');
    const [filteredRecords, setFilteredRecords] = useState<UserRecordDto['records']>([]);
    const [pageSize, setPageSize] = useState<number>(10);
    const { user } = useSelector((state: RootState) => state.auth);
    const pickerLocale = useMemo(() => {
        return i18n.language === 'tr' ? datePickerTr : datePickerEn;
    }, [i18n.language]);
    // Kayıtları getir
    const fetchUserRecords = async () => {
        if (!userId) return;

        setLoading(true);
        try {
            const numericUserId = Number(userId);

            const [userDataResponse, records] = await Promise.all([
                userService.getUserById(numericUserId),
                dailyRecordService.getUserRecords(numericUserId, dateRange[0].format('YYYY-MM-DD'), dateRange[1].format('YYYY-MM-DD'))
            ]);

            setUserData(userDataResponse);
            setUserRecords(records);

            // Kullanıcı adını statik olarak ayarlıyoruz (gerçek uygulamada API'den gelmeli)
            // setUserName(`${t('attendanceUserAdminPage.employee')} #${userId}`);
        } catch (error: any) {
            handleApiError(error, t);
        } finally {
            setLoading(false);
        }
    };

    const handleDateRangeChange = (dates: any) => {
        if (dates && dates.length === 2) {
            setDateRange([dates[0], dates[1]]);
        }
    };

    useEffect(() => {
        fetchUserRecords();
    }, [userId, dateRange]);

    // Kayıtları filtrele
    const filterRecords = (records: UserRecordDto['records'], filter: 'all' | 'onTime' | 'late') => {
        if (filter === 'all') {
            return records;
        } else if (filter === 'onTime') {
            return records.filter(record => !record.isLate);
        } else if (filter === 'late') {
            return records.filter(record => record.isLate);
        }
        return records;
    };

    // Durum filtresi değiştiğinde kayıtları filtrele
    useEffect(() => {
        if (userRecords?.records) {
            const filtered = filterRecords(userRecords.records, statusFilter);
            setFilteredRecords(filtered);
        }
    }, [userRecords, statusFilter]);

    // Durum filtresini değiştir
    const handleStatusFilterChange = (filter: 'all' | 'onTime' | 'late') => {
        setStatusFilter(filter);
    };

    // Filtrelenmiş kayıtlar temelinde dinamik istatistikleri hesapla
    const computedStatistics = useMemo(() => {
        if (!filteredRecords || filteredRecords.length === 0) {
            return {
                totalWorkMinutes: 0,
                lateDaysCount: 0,
                earlyLeaveDaysCount: 0,
                totalRecords: 0
            };
        }

        const totalWorkMinutes = filteredRecords.reduce((total, record) => {
            return total + (record.workMinutes || 0);
        }, 0);

        const lateDaysCount = filteredRecords.filter(record => record.isLate).length;

        // Early leave count - API'den gelmediyse elle hesaplayabiliriz
        // Şimdilik userRecords'den alıyoruz ama filtrelenmiş kayıtlara göre oranlanabilir
        const earlyLeaveDaysCount = filteredRecords.length > 0 && userRecords?.totalRecords
            ? Math.round((filteredRecords.length / userRecords.totalRecords) * (userRecords.earlyLeaveDaysCount || 0))
            : 0;

        return {
            totalWorkMinutes,
            lateDaysCount,
            earlyLeaveDaysCount,
            totalRecords: filteredRecords.length
        };
    }, [filteredRecords, userRecords]);

    // Kayıt düzenleme modalını aç
    const handleEditRecord = (record: UserRecordDto['records'][0]) => {
        setSelectedRecord(record);
        setEditedRecord({
            entryTime: record.entryTime ? dayjs(record.entryTime) : null,
            exitTime: record.exitTime ? dayjs(record.exitTime) : null,
            recordId: record.recordId,
        });
        setIsEditModalVisible(true);
    };

    // Saat değişikliklerini işle
    // const handleTimeChange = (field: 'entryTime' | 'exitTime', time: any) => {
    //     if (!time || !editedRecord) return;

    //     const currentDate = new Date(editedRecord[field]);
    //     const newTime = time.toDate();

    //     currentDate.setHours(newTime.getHours());
    //     currentDate.setMinutes(newTime.getMinutes());

    //     setEditedRecord({
    //         ...editedRecord,
    //         [field]: currentDate.toISOString()
    //     });
    // };

    // Kaydı güncelle
    const handleUpdateRecord = async () => {
        if (!editedRecord || !editedRecord.entryTime || !selectedRecord?.recordId) {
            message.error(t('common.errors.invalidData', "Geçersiz veri veya kayıt ID'si eksik."));
            return;
        }

        // Türkiye formatında tarih formatla (ISO yerine)
        const finalEntryTime = editedRecord.entryTime.format('YYYY-MM-DD HH:mm:ss');
        let finalExitTime: string | null = null;

        if (editedRecord.exitTime) {
            if (editedRecord.exitTime.isSameOrBefore(editedRecord.entryTime)) {
                message.error(t('attendanceUserAdminPage.errorExitBeforeEntry', "Çıkış saati, giriş saatinden sonra olmalıdır."));
                return;
            }
            finalExitTime = editedRecord.exitTime.format('YYYY-MM-DD HH:mm:ss');
        }

        setIsUpdatingRecord(true);
        try {
            // dailyRecordService.updateRecordTime servisini çağır
            await dailyRecordService.updateRecordTime(selectedRecord.recordId, finalEntryTime, finalExitTime || undefined);
            message.success(t('attendanceUserAdminPage.recordUpdatedSuccessfully'));
            setIsEditModalVisible(false);
            fetchUserRecords(); // Kayıtları yenile
        } catch (error: any) {
            handleApiError(error, t);
        } finally {
            setIsUpdatingRecord(false);
        }
    };

    // PDF dışa aktarma
    const handleExportPdf = () => {
        if (!userRecords || !userData) return;

        type PdfDataType = {
            date: string;
            entryTime: string;
            exitTime: string;
            workTime: string;
            lateDuration: string;
            excuseReason: string;
            status: string;
        };

        const columnsForPdf = [
            { header: 'Tarih', property: 'date' as keyof PdfDataType, width: 80 },
            { header: 'Giris', property: 'entryTime' as keyof PdfDataType, width: 60 },
            { header: 'Cikis', property: 'exitTime' as keyof PdfDataType, width: 60 },
            { header: 'Calisma', property: 'workTime' as keyof PdfDataType, width: 70 },
            { header: 'Gec Kalma', property: 'lateDuration' as keyof PdfDataType, width: 70 },
            { header: 'Mazeret', property: 'excuseReason' as keyof PdfDataType, width: 120 },
            { header: 'Durum', property: 'status' as keyof PdfDataType, width: 60 }
        ];

        const data = filteredRecords.map(record => ({
            date: dayjs(record.entryTime).format('DD MMMM YYYY'),
            entryTime: dayjs(record.entryTime).format('HH:mm'),
            exitTime: record.exitTime ? dayjs(record.exitTime).format('HH:mm') : '-',
            workTime: `${Math.floor(Number(record.workMinutes) / 60)}s ${Number(record.workMinutes) % 60}dk`,
            lateDuration: record.isLate ? record.lateDuration : '-',
            excuseReason: record.excuseReason ? record.excuseReason : '-',
            status: record.isLate ? 'Gec Kaldi' : 'Normal'
        }));

        const description = `${normalizeTurkishChars(userData.firstName)} ${normalizeTurkishChars(userData.lastName)} - Devam Gecmisi\n${t('common.labels.dateRange')}: ${dateRange[0].format('DD.MM.YYYY')} - ${dateRange[1].format('DD.MM.YYYY')}`;

        createPdf(data, {
            title: 'Devam Gecmisi',
            description: normalizeTurkishChars(description),
            headerColor: '#3e5569',
            accentColor: '#3498db',
            columns: columnsForPdf,
            showStatistics: true,
            statistics: [
                { label: t('attendanceUserAdminPage.totalRecords'), value: computedStatistics.totalRecords },
                { label: t('attendanceUserAdminPage.lateCount'), value: computedStatistics.lateDaysCount },
                { label: t('attendanceUserAdminPage.earlyLeaveCount'), value: computedStatistics.earlyLeaveDaysCount },
                { label: t('attendanceUserAdminPage.totalWork'), value: computedStatistics.totalWorkMinutes + 'dk' }
            ],
            footer: {
                text: `© ${new Date().getFullYear()} DINOX - Tüm haklari saklidir.`,
                showPageNumbers: true
            }
        });
    };

    // Excel dışa aktarma
    const handleExportExcel = () => {
        if (!userRecords || !userData) return;

        type ExcelColumns = {
            header: string;
            property: 'date' | 'entryTime' | 'exitTime' | 'workTime' | 'lateDuration' | 'excuseReason' | 'status';
        };

        const columnsForExcel: ExcelColumns[] = [
            { header: 'Tarih', property: 'date' },
            { header: 'Giris Saati', property: 'entryTime' },
            { header: 'Cikis Saati', property: 'exitTime' },
            { header: 'Calisma Suresi', property: 'workTime' },
            { header: 'Gec Kalma', property: 'lateDuration' },
            { header: 'Mazeret', property: 'excuseReason' },
            { header: 'Durum', property: 'status' }
        ];

        const data = filteredRecords.map(record => ({
            date: dayjs(record.entryTime).format('DD MMMM YYYY'),
            entryTime: dayjs(record.entryTime).format('HH:mm'),
            exitTime: record.exitTime ? dayjs(record.exitTime).format('HH:mm') : '-',
            workTime: `${Math.floor(Number(record.workMinutes) / 60)}s ${Number(record.workMinutes) % 60}dk`,
            lateDuration: record.isLate ? record.lateDuration : '-',
            excuseReason: record.excuseReason ? record.excuseReason : '-',
            status: record.isLate ? 'Gec Kaldi' : 'Normal'
        }));

        exportToExcel(data, `${userData.firstName}_${userData.lastName}_${t('attendanceUserAdminPage.attendanceHistory')}`, columnsForExcel);
    };

    const columns = [
        {
            title: t('attendanceUserAdminPage.date'),
            key: 'date',
            // record tipini UserRecordDto['records'][0] olarak düşün
            render: (record: UserRecordDto['records'][0]) => dayjs(record.entryTime).format('DD.MM.YYYY')
        },
        {
            title: t('attendanceUserAdminPage.entryTime'),
            key: 'entryTime',
            render: (record: UserRecordDto['records'][0]) => dayjs(record.entryTime).format('HH:mm')
        },
        {
            title: t('attendanceUserAdminPage.exitTime'),
            key: 'exitTime',
            render: (record: UserRecordDto['records'][0]) => record.exitTime ? dayjs(record.exitTime).format('HH:mm') : '--:--'
        },
        {
            title: t('attendanceUserAdminPage.status'),
            key: 'status',
            render: (record: UserRecordDto['records'][0]) => (
                <Badge
                    status={record.isLate ? "error" : "success"}
                    text={record.isLate ? t('attendanceUserAdminPage.late') : t('attendanceUserAdminPage.onTime')}
                />
            )
        },
        {
            title: t('attendanceUserAdminPage.lateDuration'),
            key: 'lateDuration',
            dataIndex: 'lateDuration'
        },
        {
            title: 'Mazeret',
            key: 'excuseReason',
            render: (record: UserRecordDto['records'][0]) => {
                return record.excuseReason ? (
                    <span className="text-muted">
                        {record.excuseReason}
                    </span>
                ) : (
                    <span className="text-muted">-</span>
                );
            }
        },
        {
            title: t('attendanceUserAdminPage.workDuration'),
            key: 'workMinutes',
            render: (record: UserRecordDto['records'][0]) => `${Math.floor(Number(record.workMinutes) / 60)} ${t('common.time.hoursShort', 'sa')} ${Number(record.workMinutes) % 60} ${t('common.time.minutesShort', 'dk')}`
        },
        {
            title: t('attendanceUserAdminPage.action'),
            key: 'action',
            render: (_: any, record: UserRecordDto['records'][0]) => ( // record tipini güncelle
                <Button
                    type="primary"
                    size="small"
                    onClick={() => handleEditRecord(record)}
                >
                    {t('attendanceUserAdminPage.edit')}
                </Button>
            )
        }
    ];

    if (loading && !userRecords) {
        return <LoadingSpinner />;
    }

    return (
        <div className="page-wrapper">
            <div className="content container-fluid">
                {/* Breadcrumb */}
                <div className="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3">
                    <div className="my-auto mb-2">
                        <h2 className="mb-1">{userData?.firstName} {userData?.lastName} - {t('attendanceUserAdminPage.attendanceHistory')}</h2>
                        <nav>
                            <ol className="breadcrumb mb-0">
                                <li className="breadcrumb-item">
                                    <Link to={all_routes.adminDashboard}>
                                        <i className="ti ti-smart-home" />
                                    </Link>
                                </li>
                                <li className="breadcrumb-item">
                                    <Link to={all_routes.employeeList}>{t('attendanceUserAdminPage.employeeList')}</Link>
                                </li>
                                <li className="breadcrumb-item active" aria-current="page">
                                    {t('attendanceUserAdminPage.attendanceHistory')}
                                </li>
                            </ol>
                        </nav>
                    </div>
                    <div className="d-flex my-xl-auto right-content align-items-center flex-wrap">
                        <div className="me-2 mb-2">
                            {user?.permissions?.includes(PermissionType.EXPORT) && (
                            <div className="dropdown">
                                <Link
                                    to="#"
                                    className="dropdown-toggle btn btn-white d-inline-flex align-items-center"
                                    data-bs-toggle="dropdown"
                                >
                                    <i className="ti ti-file-export me-1" />
                                    {t('common.buttons.export')}
                                </Link>
                                <ul className="dropdown-menu dropdown-menu-end p-3">
                                    <li>
                                        <Link to="#" className="dropdown-item rounded-1" onClick={handleExportPdf}>
                                            <i className="ti ti-file-type-pdf me-1" />
                                            {t('common.buttons.pdf')}
                                        </Link>
                                    </li>
                                    <li>
                                        <Link to="#" className="dropdown-item rounded-1" onClick={handleExportExcel}>
                                            <i className="ti ti-file-type-xls me-1" />
                                            {t('common.buttons.excel')}
                                        </Link>
                                    </li>
                                </ul>
                            </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* İstatistik Kartları */}
                <div className="row">
                    <div className="col-xl-3 col-md-6">
                        <div className="card h-100 border-0 shadow-sm hover-shadow">
                            <div className="card-body text-center p-4">
                                <div className="mb-4">
                                    <div className="avatar avatar-xl bg-primary-subtle rounded-circle d-inline-flex align-items-center justify-content-center">
                                        <span className="fs-1"><i className="ti ti-clock fs-2"></i></span>
                                    </div>
                                </div>
                                <h2 className="mb-2 text-primary">
                                    {computedStatistics.totalWorkMinutes ?
                                        `${Math.floor(computedStatistics.totalWorkMinutes / 60)}s ${computedStatistics.totalWorkMinutes % 60}dk` :
                                        '0s 0dk'}
                                </h2>
                                <p className="fw-medium mb-0 text-muted">Seçilen Tarih Aralığında Çalışılan Süre</p>
                            </div>
                        </div>
                    </div>
                    <div className="col-xl-3 col-md-6">
                        <div className="card h-100 border-0 shadow-sm hover-shadow">
                            <div className="card-body text-center p-4">
                                <div className="mb-4">
                                    <div className="avatar avatar-xl bg-danger-subtle rounded-circle d-inline-flex align-items-center justify-content-center">
                                        <span className="fs-2"><i className="ti ti-clock-exclamation fs-2"></i></span>
                                    </div>
                                </div>
                                <h2 className="mb-2 text-danger">{computedStatistics.lateDaysCount}</h2>
                                <p className="fw-medium mb-0 text-muted">Seçilen Tarih Aralığında Geç Kalınan Gün Sayısı</p>
                            </div>
                        </div>
                    </div>
                    <div className="col-xl-3 col-md-6">
                        <div className="card h-100 border-0 shadow-sm hover-shadow">
                            <div className="card-body text-center p-4">
                                <div className="mb-4">
                                    <div className="avatar avatar-xl bg-warning-subtle rounded-circle d-inline-flex align-items-center justify-content-center">
                                        <span className="fs-2"><i className="ti ti-clock-minus fs-2"></i></span>
                                    </div>
                                </div>
                                <h2 className="mb-2 text-warning">{computedStatistics.earlyLeaveDaysCount}</h2>
                                <p className="fw-medium mb-0 text-muted">Seçilen Tarih Aralığında Erken Çıkış Yapılan Gün Sayısı</p>
                            </div>
                        </div>
                    </div>
                    <div className="col-xl-3 col-md-6">
                        <div className="card h-100 border-0 shadow-sm hover-shadow">
                            <div className="card-body text-center p-4">
                                <div className="mb-4">
                                    <div className="avatar avatar-xl bg-success-subtle rounded-circle d-inline-flex align-items-center justify-content-center">
                                        <span className="fs-2"><i className="ti ti-clipboard-check fs-2"></i></span>
                                    </div>
                                </div>
                                <h2 className="mb-2 text-success">{computedStatistics.totalRecords}</h2>
                                <p className="fw-medium mb-0 text-muted">Seçilen Tarih Aralığında Çalışılan Gün Sayısı</p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Devam Kayıtları Listesi */}
                <div className="card">
                    <div className="card-header d-flex align-items-center justify-content-between flex-wrap row-gap-3">
                        <div className="d-flex align-items-center">
                            <h5 className="mb-0 me-3">{t('attendanceUserAdminPage.attendanceHistory')}</h5>
                            <div className="dataTables_length">
                                <label className="d-flex align-items-center text-muted">
                                    <span className="me-2">Sayfa Başına</span>
                                    <select
                                        className="form-select form-select-sm"
                                        style={{ width: "70px" }}
                                        value={pageSize}
                                        onChange={(e) => setPageSize(Number(e.target.value))}
                                    >
                                        <option value="10">10</option>
                                        <option value="25">25</option>
                                        <option value="50">50</option>
                                        <option value="100">100</option>
                                    </select>
                                    <span className="ms-2">kayıt</span>
                                </label>
                            </div>
                        </div>
                        <div className="d-flex my-xl-auto right-content align-items-center flex-wrap row-gap-3">
                            <div className="me-3">
                                <RangePicker
                                    format="DD-MM-YYYY"
                                    defaultValue={dateRange}
                                    onChange={handleDateRangeChange}
                                />
                            </div>
                            <div className="dropdown me-3">
                                <Link
                                    to="#"
                                    className="dropdown-toggle btn btn-sm btn-white d-inline-flex align-items-center"
                                    data-bs-toggle="dropdown"
                                >
                                    <i className="ti ti-filter me-1"></i>
                                    {statusFilter === 'all' ? t('attendanceUserAdminPage.allStatus') :
                                        statusFilter === 'onTime' ? t('attendanceUserAdminPage.onTimeStatus') :
                                            t('attendanceUserAdminPage.lateStatus')}
                                </Link>
                                <ul className="dropdown-menu dropdown-menu-end p-3">
                                    <li>
                                        <Link
                                            to="#"
                                            className={`dropdown-item rounded-1 ${statusFilter === 'all' ? 'active' : ''}`}
                                            onClick={(e) => {
                                                e.preventDefault();
                                                handleStatusFilterChange('all');
                                            }}
                                        >
                                            <i className="ti ti-list me-2"></i>
                                            {t('attendanceUserAdminPage.all')}
                                        </Link>
                                    </li>
                                    <li>
                                        <Link
                                            to="#"
                                            className={`dropdown-item rounded-1 ${statusFilter === 'onTime' ? 'active' : ''}`}
                                            onClick={(e) => {
                                                e.preventDefault();
                                                handleStatusFilterChange('onTime');
                                            }}
                                        >
                                            <i className="ti ti-clock-check me-2 text-success"></i>
                                            {t('attendanceUserAdminPage.onTimeStatus')}
                                        </Link>
                                    </li>
                                    <li>
                                        <Link
                                            to="#"
                                            className={`dropdown-item rounded-1 ${statusFilter === 'late' ? 'active' : ''}`}
                                            onClick={(e) => {
                                                e.preventDefault();
                                                handleStatusFilterChange('late');
                                            }}
                                        >
                                            <i className="ti ti-clock-exclamation me-2 text-danger"></i>
                                            {t('attendanceUserAdminPage.lateStatus')}
                                        </Link>
                                    </li>
                                </ul>
                            </div>
                        </div>
                    </div>
                    <div className="card-body">
                        {loading ? (
                            <LoadingSpinner />
                        ) : error ? (
                            <div className="alert alert-danger">{error}</div>
                        ) : filteredRecords.length === 0 ? (
                            <div className="text-center p-5">
                                <i className="ti ti-calendar-off fs-2 text-muted mb-3 d-block"></i>
                                <Text type="secondary">
                                    {statusFilter === 'all'
                                        ? t('attendanceUserAdminPage.noAttendance')
                                        : statusFilter === 'onTime'
                                            ? 'Seçilen tarih aralığında zamanında gelen kayıt bulunamadı.'
                                            : 'Seçilen tarih aralığında geç kalınan kayıt bulunamadı.'
                                    }
                                </Text>
                            </div>
                        ) : (
                            <Table
                                dataSource={filteredRecords}
                                columns={columns}
                                rowKey={(record) => record.entryTime}
                                pagination={{ pageSize: pageSize }}
                                className="table-striped"
                            />
                        )}
                    </div>
                </div>
            </div>

            {/* Düzenleme Modalı */}
            <Modal
                title={
                    <div className="d-flex align-items-center">
                        <i className="ti ti-edit fs-4 me-2 text-primary"></i>
                        <span>{t('attendanceUserAdminPage.editAttendance')}</span>
                    </div>
                }
                open={isEditModalVisible}
                onCancel={() => setIsEditModalVisible(false)}
                footer={[
                    <Button
                        key="cancel"
                        onClick={() => setIsEditModalVisible(false)}
                    >
                        {t('common.buttons.cancel')}
                    </Button>,
                    <Button
                        key="submit"
                        type="primary"
                        loading={isUpdatingRecord}
                        onClick={handleUpdateRecord}
                    >
                        {t('common.buttons.update')}
                    </Button>
                ]}
                width={500}
            >
                <div className="edit-record-form">
                    <div className="mb-4">
                        <h6 className="text-muted mb-3">{t('attendanceUserAdminPage.date')}</h6>
                        <div className="p-3 bg-light rounded-3 border">
                            <p className="mb-0 fw-medium">
                                {/* Giriş Tarihi (Düzenlenemez) */}
                                {editedRecord?.entryTime ? editedRecord.entryTime.format('dddd, DD MMMM YYYY') : ''}
                            </p>
                        </div>
                    </div>
                    <div className="time-picker-container p-4 bg-light rounded-3 border">
                        <div className="d-flex align-items-center mb-4">
                            <div className="avatar avatar-sm bg-primary-subtle rounded-circle d-inline-flex align-items-center justify-content-center me-2">
                                <i className="ti ti-arrow-enter text-primary"></i>
                            </div>
                            <h6 className="mb-0">{t('attendanceUserAdminPage.workHours')}</h6>
                        </div>
                        <div className="time-display">
                            <div className="row g-4">
                                <div className="col-6">
                                    <div className="time-card p-4 bg-white rounded-3 border text-center">
                                        <div className="time-header mb-4">
                                            <div className="time-icon mb-3">
                                                <div className="avatar avatar-lg bg-primary-subtle rounded-circle d-inline-flex align-items-center justify-content-center">
                                                    <i className="ti ti-arrow-enter text-primary fs-4"></i>
                                                </div>
                                            </div>
                                            <h6 className="text-primary mb-0">{t('attendanceUserAdminPage.entryTime')}</h6>
                                        </div>
                                        <DatePicker.TimePicker // Ant Design'ın TimePicker'ını kullanıyoruz
                                            className="time-picker-input"
                                            format="HH:mm"
                                            value={editedRecord?.entryTime}
                                            onChange={(time) => {
                                                if (time && editedRecord?.entryTime) {
                                                    const newEntryTime = editedRecord.entryTime
                                                        .hour(time.hour())
                                                        .minute(time.minute())
                                                        .second(0)
                                                        .millisecond(0);
                                                    setEditedRecord({
                                                        ...editedRecord,
                                                        entryTime: newEntryTime
                                                    });
                                                }
                                            }}
                                            minuteStep={1}
                                            showNow={false}
                                            allowClear={false}
                                            placeholder={t('attendanceUserAdminPage.selectTime')}
                                            popupClassName="time-picker-popup"
                                            locale={pickerLocale}
                                        />
                                    </div>
                                </div>
                                <div className="col-6">
                                    <div className="time-card p-4 bg-white rounded-3 border text-center">
                                        <div className="time-header mb-4">
                                            <div className="time-icon mb-3">
                                                <div className="avatar avatar-lg bg-danger-subtle rounded-circle d-inline-flex align-items-center justify-content-center">
                                                    <i className="ti ti-arrow-exit text-danger fs-4"></i>
                                                </div>
                                            </div>
                                            <h6 className="text-danger mb-0">{t('attendanceUserAdminPage.exitTime')}</h6>
                                        </div>
                                        <DatePicker // Ant Design DatePicker
                                            className="time-picker-input"
                                            showTime={{ format: 'HH:mm' }}
                                            format="DD.MM.YYYY HH:mm"
                                            value={editedRecord?.exitTime}
                                            onChange={(dateTime) => {
                                                setEditedRecord((prev: EditedAttendanceRecord | null) =>
                                                    prev ? { ...prev, exitTime: dateTime } : null
                                                );
                                            }}
                                            minuteStep={1}
                                            showNow={false}
                                            allowClear={true}
                                            placeholder={t('attendanceUserAdminPage.selectDateTimePlaceholder', "Tarih ve saat seçin")}
                                            locale={pickerLocale}
                                            disabledDate={(current) => {
                                                if (!editedRecord?.entryTime) return false;
                                                return current && current.isBefore(editedRecord.entryTime.startOf('day'));
                                            }}
                                            disabledTime={(current) => {
                                                if (!editedRecord?.entryTime || !current) return {};
                                                if (current.isSame(editedRecord.entryTime, 'day')) {
                                                    const entryHour = editedRecord.entryTime.hour();
                                                    const entryMinute = editedRecord.entryTime.minute();
                                                    return {
                                                        disabledHours: () => Array.from({ length: entryHour }, (_, i) => i),
                                                        disabledMinutes: (selectedHour) => {
                                                            if (selectedHour === entryHour) {
                                                                return Array.from({ length: entryMinute + 1 }, (_, i) => i);
                                                            }
                                                            return [];
                                                        },
                                                    };
                                                }
                                                return {};
                                            }}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <style>
                        {`
                            .time-picker-container {
                                transition: all 0.3s;
                                background: linear-gradient(145deg, #f8f9fa, #ffffff);
                            }
                            .time-picker-container:hover {
                                transform: translateY(-2px);
                                box-shadow: 0 8px 24px rgba(0,0,0,0.08);
                            }
                            .time-card {
                                transition: all 0.3s;
                                height: 100%;
                                background: #ffffff;
                                border: 1px solid rgba(0,0,0,0.05);
                            }
                            .time-card:hover {
                                transform: translateY(-2px);
                                box-shadow: 0 4px 16px rgba(0,0,0,0.06);
                                border-color: rgba(67, 97, 238, 0.2);
                            }
                            .time-header {
                                transition: all 0.3s;
                            }
                            .time-icon {
                                transition: all 0.3s;
                            }
                            .time-card:hover .time-icon {
                                transform: scale(1.05);
                            }
                            .time-picker-input {
                                width: 100%;
                                height: 48px;
                                font-size: 1.1rem;
                                border-radius: 8px;
                                border: 2px solid #e9ecef;
                                transition: all 0.3s;
                            }
                            .time-picker-input:hover {
                                border-color: #4361ee;
                            }
                            .time-picker-input:focus {
                                border-color: #4361ee;
                                box-shadow: 0 0 0 4px rgba(67, 97, 238, 0.1);
                            }
                            .time-picker-popup .ant-picker-panel {
                                border-radius: 8px;
                            }
                            .time-picker-popup .ant-picker-time-panel-column {
                                scrollbar-width: thin;
                                scrollbar-color: #4361ee #f8f9fa;
                            }
                            .time-picker-popup .ant-picker-time-panel-column::-webkit-scrollbar {
                                width: 6px;
                            }
                            .time-picker-popup .ant-picker-time-panel-column::-webkit-scrollbar-track {
                                background: #f8f9fa;
                                border-radius: 3px;
                            }
                            .time-picker-popup .ant-picker-time-panel-column::-webkit-scrollbar-thumb {
                                background-color: #4361ee;
                                border-radius: 3px;
                            }
                            .ant-picker-ok {
                                margin-top: 8px;
                            }
                            .ant-picker-ok button {
                                background-color: #4361ee;
                                border-color: #4361ee;
                            }
                            .ant-picker-ok button:hover {
                                background-color: #3651d4;
                                border-color: #3651d4;
                            }
                        `}
                    </style>
                </div>
            </Modal>

            {/* Footer */}
            <div className="footer d-sm-flex align-items-center justify-content-between border-top bg-white p-3">
                <FooterSection />
            </div>
        </div>
    );
};

export default AllAttendanceUser;